import React, { useState, useEffect } from 'react';
import { X, Upload, FileText, Trash2, Eye, Lock, Globe, Share2, Loader2, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import FilePreviewModal from './FilePreviewModal';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next'; 

const AttachmentsModal = ({ isOpen, onClose, ownertable, ownerid }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const currentUser = localStorage.getItem('username');

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/attachments/${ownertable}/${ownerid}?current_user=${currentUser}`);
      setDocs(res.data);
    } catch (err) { toast.error(t('attachmentsModal.loadError')); } // 🎯
    setLoading(false);
  };

  useEffect(() => { if (isOpen) fetchDocs(); }, [isOpen, ownerid]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('ownertable', ownertable);
    formData.append('ownerid', ownerid);
    formData.append('createby', currentUser);

    setUploading(true);
    try {
      await axios.post('/api/attachments/upload', formData);
      toast.success(t('attachmentsModal.uploadSuccess')); // 🎯
      fetchDocs();
    } catch (err) { toast.error(t('attachmentsModal.uploadFailed')); } // 🎯
    setUploading(false);
  };

  const handleDelete = async (doc) => {
      const result = await Swal.fire({
          title: t('attachmentsModal.deleteConfirmTitle'), // 🎯
          text: t('attachmentsModal.deleteConfirmText', { name: doc.document_name }), // 🎯 ديناميكي
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#1a3353', 
          cancelButtonColor: '#d33',
          confirmButtonText: t('attachmentsModal.btnYesDelete'), // 🎯
          cancelButtonText: t('attachmentsModal.btnCancel'), // 🎯
          reverseButtons: true, 
          didOpen: () => {
            const container = Swal.getContainer();
            if (container) {
              container.style.zIndex = '7000';
            }
          },
          customClass: {
              popup: 'font-sans text-[11px] uppercase tracking-tighter', 
              title: 'font-black text-blue-900',
              confirmButton: 'font-bold text-[10px]',
              cancelButton: 'font-bold text-[10px]'
          }
      });
  
      if (result.isConfirmed) {
          const loadId = toast.loading(t('attachmentsModal.processingDelete')); // 🎯
          try {
              await axios.delete(`/api/attachments/delete/${doc.id}?current_user=${currentUser}`);
              
              Swal.fire({
                  title: t('attachmentsModal.deletedTitle'), // 🎯
                  text: t('attachmentsModal.deletedText'), // 🎯
                  icon: 'success',
                  timer: 2000,
                  showConfirmButton: false,
                  customClass: { popup: 'font-sans text-[11px] uppercase' }
              });
  
              toast.dismiss(loadId);
              fetchDocs(); 
          } catch (err) {
              toast.error(err.response?.data?.detail || t('attachmentsModal.deleteFailed'), { id: loadId }); // 🎯
          }
      }
  };
  
  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-[5000] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-200 uppercase ${isRTL ? 'font-cairo dir-rtl' : 'font-sans'}`}>
      <div className="bg-white w-[850px] h-[600px] shadow-2xl rounded-sm overflow-hidden flex flex-col border border-gray-300 animate-in zoom-in-95">
        
        {/* Header */}
        <div className="bg-[#1a3353] text-white p-4 flex justify-between items-center shadow-md">
          <div className="flex items-center gap-3">
            <FileText size={20} className="text-blue-300" />
            <div className="flex flex-col">
                <span className="text-[1.0em] font-black tracking-widest uppercase italic">{t('attachmentsModal.headerTitle')}</span>
                <span className="text-[0.9em] text-blue-300 font-bold tracking-tighter">{t('attachmentsModal.linkingTo')} {ownertable} / {ownerid}</span>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-red-600 p-1 rounded-full transition-colors text-white"><X size={20}/></button>
        </div>

        {/* Toolbar */}
        <div className="p-4 bg-gray-50 border-b flex justify-between items-center px-8">
            <span className="text-[1.0em] font-black text-blue-900 tracking-widest">{t('attachmentsModal.filesFound')} {docs.length}</span>
            
            {/* 🎯 الغلاف الخارجي للزر عشان الـ label يشتغل صح */}
            <div className="relative">
              <input type="file" id="file-upload" className="hidden" onChange={handleFileUpload} disabled={uploading} />
              <label htmlFor="file-upload" className={`bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-sm font-black text-[1.0em] flex items-center gap-2 cursor-pointer shadow-lg transition-all active:scale-95 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  {uploading ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14} />}
                  {t('attachmentsModal.uploadBtn')}
              </label>
            </div>
        </div>

        {/* Content Table */}
        <div className="flex-1 overflow-auto bg-white">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-100 border-b text-gray-400 font-black text-[1.0em] sticky top-0 uppercase tracking-tighter">
              <tr>
                <th className="p-4 border-r w-1/2">{t('attachmentsModal.thName')}</th>
                <th className="p-4 border-r">{t('attachmentsModal.thOwner')}</th>
                <th className="p-4 border-r text-center">{t('attachmentsModal.thStatus')}</th>
                <th className="p-4 text-center">{t('attachmentsModal.thActions')}</th>
              </tr>
            </thead>
            <tbody className="text-[1.0em] font-bold">
              {docs.map((doc) => (
                <tr key={doc.id} className="border-b hover:bg-blue-50/50 transition-colors group">
                  <td className="p-4 border-r text-blue-900 font-black uppercase flex items-center gap-2">
                     <FileText size={14} className="text-gray-300 group-hover:text-blue-600"/> {doc.document_name}
                  </td>
                  <td className="p-4 border-r text-gray-500 uppercase">{doc.createby}</td>
                  <td className="p-4 border-r text-center">
                     {doc.is_private ? 
                        <span className="bg-red-50 text-red-600 px-2 py-1 rounded text-[8px] flex items-center justify-center gap-1"><Lock size={10}/> {t('attachmentsModal.statusPrivate')}</span> : 
                        <span className="bg-green-50 text-green-700 px-2 py-1 rounded text-[8px] flex items-center justify-center gap-1"><Globe size={10}/> {t('attachmentsModal.statusPublic')}</span>
                     }
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex justify-center gap-4">
                      <button onClick={() => setPreviewFile(doc)} className="text-blue-600 hover:scale-125 transition-transform"><Eye size={16}/></button>
                      <button className="text-orange-600 hover:scale-125 transition-transform"><Share2 size={16}/></button>
                      {doc.createby === currentUser && (
                        <button className="text-red-300 hover:text-red-600 transition-colors" onClick={() => handleDelete(doc)}><Trash2 size={16}/></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {docs.length === 0 && !loading && (
             <div className="h-full flex flex-col items-center justify-center text-gray-300 py-20 italic">
                <ShieldCheck size={48} strokeWidth={1} className="mb-4 opacity-20" />
                <span className="uppercase font-black tracking-widest">{t('attachmentsModal.noDocs')}</span>
             </div>
          )}
        </div>

        {/* Preview Sub-Modal */}
        <FilePreviewModal 
            isOpen={!!previewFile} 
            file={previewFile} 
            onClose={() => setPreviewFile(null)} 
        />
      </div>
    </div>
  );
};

export default AttachmentsModal;